// Case workspace: header + stepper + tabs (overview / timeline / notes / files / approvals /
// production / dispatch) + always-visible action rail. Everything shown comes from ONE master record.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { WF_STAGE_ROLES as R, formatFileSize, WF_APPROVAL_KIND_LABELS, WF_APPROVAL_LABELS, WF_NOTE_KINDS, WF_PRIORITY_LABELS, WF_REQ_LABELS, WF_REQ_STATUSES, alignerDiscountOf, alignerQuote, isDerivedDueDate, turnaroundDueDate, type WfCase, type WfCasePatch, type WfCaseWorkspace, type WfFile,
  type WfStage, type WfEvent } from '@alignova/shared';
import { useCan } from '../../auth/useCan.ts';
import { DiscountInput, QuoteLine } from './DiscountInput.tsx';
import { CaseChat } from '../../ui/CaseChat.tsx';
import { PatientAccessCard } from './PatientAccessCard.tsx';
import { lazy, Suspense } from 'react';
import { isModelFile } from '../../ui/modelFiles.ts';
const StlViewer = lazy(() => import('../../ui/StlViewer.tsx').then((m) => ({ default: m.StlViewer })));
import { useLiveEvent } from '../../lib/useLive.ts';
import { useAlignerPricing } from '../../lib/useAlignerPricing.ts';
import { fileDownloadUrl, historyExportUrl, useWfConfig, wfApi } from './workflowApi.ts';
import { CaseActionModal, type ActionTarget, type WfAction } from './WfActions.tsx';
import { AgeBadge, Btn, DueBadge, Empty, Field, Modal, PriorityPill, ReqBadge, StagePill, Tabs, fmtD, fmtDT, hours, inputCls } from './wfUi.tsx';
import { CompliancePanel, TrayCountRequired } from './WfCompliance.tsx';
import { ArchTrayInputs, PRODUCTION_COUNT_STAGES, ProductionCounters, ProformaPrintButton, alignerSummary } from './WfProforma.tsx';
import { SetupPanel } from './WfSetup.tsx';
import { TurnaroundPicker } from '../../ui/TurnaroundPicker.tsx';

type Tab = 'overview' | 'timeline' | 'notes' | 'files' | 'setup' | 'compliance' | 'approvals' | 'production' | 'dispatch' | 'chat';

export function WfCaseDetailPage() {
  const { id = '' } = useParams();
  const can = useCan();
  const cfg = useWfConfig();
  const [ws, setWs] = useState<WfCaseWorkspace | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [target, setTarget] = useState<ActionTarget | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);
  const nav = useNavigate();
  const load = useCallback(() => {
    wfApi.get(id).then((w) => { setWs(w); setErr(null); }).catch((e) => setErr(e.message));
  }, [id]);
  useEffect(load, [load]);
  useLiveEvent<{ id: string }>('workflow.changed', (p) => { if (!p?.id || p.id === id) load(); });

  if (err) return <div className="text-status-urgent-fg">{err}</div>;
  if (!ws) return <div className="text-muted">Loading case…</div>;
  const c = ws.case;
  const stage = ws.stage;
  const act = (action: WfAction, extra?: Partial<ActionTarget>) => setTarget({ action, c, ...extra });
  const canMove = can('workflow:move');

  /**
   * Delete the case.
   *
   * It is a soft delete on the server — the row, its history and its files stay, and the case drops
   * out of every list. That is worth saying in the prompt rather than implying something permanent:
   * a person who believes a button is irreversible hesitates over the wrong things, and a person
   * who believes it is reversible when it is not loses work.
   */
  const doDelete = async () => {
    setDeleting(true);
    setDeleteErr(null);
    try {
      await wfApi.remove(c.id);
      setConfirmDelete(false);
      nav('/workflow/cases');
    } catch (e) {
      setDeleteErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* ---- Phone: app-bar + hero + action strip ---- */}
      <MobileCaseHeader ws={ws} act={act} canMove={canMove} onDelete={() => setConfirmDelete(true)} />

      {/* Header (desktop) */}
      <div className="hidden rounded-lg border border-line bg-surface p-4 shadow-level-1 lg:block">
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight text-ink">{c.patientName}</h1>
              <PriorityPill p={c.priority} />
              <StagePill stageKey={c.stageKey} />
              <AgeBadge c={c} />
              {c.deletedAt && <span className="rounded bg-status-urgent-bg px-2 text-xs text-status-urgent-fg">DELETED</span>}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted">
              <span>Group ID <b className="font-mono text-ink">{c.groupId ?? '-'}</b></span>
              <span>Case <b className="font-mono text-ink">{c.caseNumber}</b></span>
              {/* The clinic, as a way into its other cases. "What else has this practice sent us"
                  is the question staff ask on the phone, and it was previously answered by
                  retyping the clinic's name into a filter. */}
              {c.clinicName && (
                <Link
                  to={`/workflow/cases?clinic=${encodeURIComponent(c.clinicName)}`}
                  className="tap font-medium text-accent hover:underline"
                  title={`All cases from ${c.clinicName}`}
                >
                  {c.clinicName}
                </Link>
              )}
              <span>{c.caseTypeRaw ?? c.caseTypes.map((t) => cfg.optionLabel('case_type', t)).join(', ')}</span>
              {c.receivedThrough && <span>via {cfg.optionLabel('received_through', c.receivedThrough)}</span>}
              <span>Received {fmtDT(c.receivedAt)}</span>
              <DueBadge c={c} />
              <span>Assigned: <b className="text-ink">{c.assignedToName ?? 'Unassigned'}</b></span>
              {c.reviewerName && <span>Reviewer: <b className="text-ink">{c.reviewerName}</b></span>}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <ReqBadge label="IPR" s={c.iprStatus} />
              <ReqBadge label="Etching guide" s={c.etchingStatus} />
              {c.approvalState !== 'none' && <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[11px]">Doctor approval: <b>{WF_APPROVAL_LABELS[c.approvalState]}</b></span>}
              {c.productionApproved && <span className="rounded bg-status-progress-bg px-1.5 py-0.5 text-[11px] text-status-progress-fg">Production approved</span>}
              {c.setupVersion > 0 && <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[11px]">Setup V{c.setupVersion}</span>}
              {c.reprintCount > 0 && <span className="rounded bg-status-urgent-bg px-1.5 py-0.5 text-[11px] text-status-urgent-fg">Reprints ×{c.reprintCount}</span>}
              {c.reworkCount > 0 && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-800">Rework ×{c.reworkCount}</span>}
              {c.holdReason && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-800">On hold: {cfg.optionLabel('hold_reason', c.holdReason)} since {fmtD(c.holdSince)}</span>}
              {!approvalGranted(c) && ws.stageVisits.some((v) => v.stageKey === R.doctorApproval) && !c.productionApproved && !cfg.stageMap.get(c.stageKey)?.isTerminal && (
                <span className="rounded border border-status-warn-fg/30 bg-status-warn-bg px-1.5 py-0.5 text-[11px] font-semibold text-status-warn-fg" title="The case cannot enter Printing until the doctor approves the setup or a production approval is recorded">
                  ⏳ Not approved yet - Printing is blocked
                </span>
              )}
              {c.cancelReason && <span className="rounded bg-status-urgent-bg px-1.5 py-0.5 text-[11px] text-status-urgent-fg">Cancelled: {cfg.optionLabel('cancel_reason', c.cancelReason)}</span>}
            </div>
          </div>
          {/* Action rail */}
          <div className="flex flex-wrap gap-1.5">
            {canMove && !stage?.isTerminal && c.stageKey !== 'on_hold' && <Btn tone="accent" onClick={() => act('move')}>Move stage</Btn>}
            {canMove && c.stageKey === 'on_hold' && <Btn tone="accent" onClick={() => act('resume')}>Resume</Btn>}
            {canMove && !stage?.isTerminal && c.stageKey !== 'on_hold' && <Btn tone="ghost" onClick={() => act('hold')}>Hold</Btn>}
            {can('workflow:update') && <Btn tone="ghost" onClick={() => act('assign')}>Assign</Btn>}
            {can('workflow:update') && <Btn tone="ghost" onClick={() => act('note')}>Note</Btn>}
            {can('workflow:update') && <Btn tone="ghost" onClick={() => act('upload')}>Upload</Btn>}
            {can('workflow:update') && <Btn tone="ghost" onClick={() => act('edit_due')}>Due / priority</Btn>}
            {can('workflow:approve') && !stage?.isTerminal && <Btn tone="ghost" onClick={() => act('request_approval')}>Request approval</Btn>}
            {can('workflow:approve') && !stage?.isTerminal && <Btn tone="ghost" onClick={() => act('approve')}>Approval decision</Btn>}
            {can('workflow:review') && !stage?.isTerminal && <Btn tone="ghost" onClick={() => act('review')}>Doctor review</Btn>}
            {can('workflow:production') && !stage?.isTerminal && <Btn tone="ghost" onClick={() => act('production')}>Production</Btn>}
            {can('workflow:production') && !stage?.isTerminal && <Btn tone="ghost" onClick={() => act('reprint')}>Reprint</Btn>}
            {can('workflow:production') && !stage?.isTerminal && <Btn tone="ghost" onClick={() => act('rework')}>Rework</Btn>}
            {can('workflow:dispatch') && (c.stageKey === R.packaging || c.stageKey === R.dispatch) && <Btn tone="accent" onClick={() => act('dispatch')}>{c.stageKey === R.dispatch ? 'Update tracking' : 'Dispatch'}</Btn>}
            {can('workflow:cancel') && !stage?.isTerminal && <Btn tone="danger" onClick={() => act('cancel')}>Cancel</Btn>}
            {can('workflow:cancel') && stage?.isTerminal && <Btn tone="ghost" onClick={() => act('reopen')}>Reopen</Btn>}
            {/* Last in the rail, and the only danger-toned control on it: a destructive action
                should never sit next to the one somebody presses forty times a day. */}
            {can('workflow:cancel') && !c.deletedAt && (
              <Btn tone="danger" onClick={() => setConfirmDelete(true)}>Delete case</Btn>
            )}
          </div>
        </div>
      </div>

      <div className="hidden lg:block">
        <Stepper ws={ws} onJump={(k) => canMove && act('move', { toStage: k })} />
      </div>

      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'overview', label: 'Overview' },
          { key: 'timeline', label: 'Timeline', count: ws.events.length },
          { key: 'notes', label: 'Notes', count: ws.notes.length },
          { key: 'files', label: 'Files', count: ws.files.length },
          { key: 'setup', label: '3D setup', count: ws.case.setupVersion || undefined },
          { key: 'compliance', label: 'Wear & photos' },
          { key: 'approvals', label: 'Approvals & reviews', count: ws.approvals.length + ws.reviews.length + ws.setupVersions.length },
          { key: 'production', label: 'Production', count: ws.production.length },
          { key: 'dispatch', label: 'Dispatch' },
          { key: 'chat', label: 'Clinic chat' },
        ]}
      />
      {tab === 'overview' && <Overview ws={ws} onSaved={load} />}
      {tab === 'timeline' && <Timeline ws={ws} />}
      {tab === 'notes' && <Notes ws={ws} onChange={load} onAdd={() => act('note')} />}
      {tab === 'files' && <Files ws={ws} onAdd={() => act('upload')} onChanged={load} />}
      {tab === 'setup' && <SetupPanel ws={ws} onChanged={load} />}
      {tab === 'compliance' && (
        <CompliancePanel caseId={ws.case.id} files={ws.files} trays={ws.case.trays} priority={ws.case.priority} onFilesChanged={load} />
      )}
      {tab === 'approvals' && <Approvals ws={ws} act={act} onChange={load} />}
      {tab === 'production' && <Production ws={ws} act={act} onChange={load} />}
      {tab === 'dispatch' && <Dispatch ws={ws} act={act} />}
      {tab === 'chat' && (
        <div className="max-w-3xl space-y-2">
          <p className="text-xs text-muted">
            This thread is shared with {ws.case.clinicName ?? ws.case.doctorName ?? 'the clinic'} - they see it live in their portal.
          </p>
          <CaseChat caseId={ws.case.id} side="lab" load={wfApi.messages} send={wfApi.sendMessage} />
        </div>
      )}

      <Modal
        open={confirmDelete}
        onClose={() => !deleting && setConfirmDelete(false)}
        title="Delete this case?"
        footer={
          <>
            <Btn tone="ghost" disabled={deleting} onClick={() => setConfirmDelete(false)}>
              No, keep it
            </Btn>
            <Btn tone="danger" disabled={deleting} onClick={() => void doDelete()}>
              {deleting ? 'Deleting…' : 'Yes, delete'}
            </Btn>
          </>
        }
      >
        <p className="text-sm text-ink">
          <b>{c.patientName}</b> - <span className="font-mono">{c.caseNumber}</span>
          {c.doctorName ? ` · ${c.doctorName}` : ''}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-muted">
          The case disappears from every list and board. Its history, files and any invoice already
          raised are kept, and a super-admin can bring it back - so this is recoverable, but the
          clinic will stop seeing it in their portal straight away.
        </p>
        {deleteErr && (
          <p className="mt-3 rounded-md bg-status-urgent-bg px-3 py-2 text-sm text-status-urgent-fg">{deleteErr}</p>
        )}
      </Modal>

      <CaseActionModal target={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); }} />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

const STAGE_GROUP_LABELS: Record<string, string> = {
  intake: 'Intake',
  records: 'Records',
  design: 'Design',
  review: 'Doctor review',
  approval: 'Approval',
  billing: 'Invoice',
  production: 'Production',
  finishing: 'Finishing',
  dispatch: 'Dispatch',
  terminal: 'Closed',
  special: 'Special',
};

/**
 * Progress track for one case.
 *
 * The configured stage list is an ORDERED CATALOGUE, not a pipeline every case walks. Several
 * stages in the same group are alternatives — a case goes to "For Mubeen / Awais Review" OR
 * "For Dr Ali Review", never both; likewise "Awaiting Records" vs "From Previous Records". Drawing
 * them as consecutive steps wrongly implied each one is a mandatory hop.
 *
 * So: a run of same-group stages the case hasn't touched collapses into ONE choice node showing the
 * group. As soon as the case enters (or has visited) one of them, that run expands to show exactly
 * the stages it actually used — the real path, not the catalogue.
 */
function Stepper({ ws, onJump }: { ws: WfCaseWorkspace; onJump: (k: string) => void }) {
  const cfg = useWfConfig();
  const visited = useMemo(() => new Set(ws.stageVisits.map((v) => v.stageKey)), [ws.stageVisits]);
  const cur = ws.case.stageKey;

  const nodes = useMemo(() => {
    const path = cfg.stages.filter((s) => s.isActive && s.group !== 'special' && s.key !== 'case_cancelled');
    const touched = (k: string) => visited.has(k) || k === cur;
    const out: Array<{ kind: 'stage'; stage: WfStage } | { kind: 'choice'; group: string; stages: WfStage[] }> = [];
    for (let i = 0; i < path.length; ) {
      const group = path[i]!.group;
      let j = i;
      while (j < path.length && path[j]!.group === group) j++;
      const run = path.slice(i, j);
      if (run.length > 1 && !run.some((r) => touched(r.key))) out.push({ kind: 'choice', group, stages: run });
      else for (const st of run) out.push({ kind: 'stage', stage: st });
      i = j;
    }
    // Special queues and cancellation only appear once the case has actually been there.
    for (const st of cfg.stages) {
      if (st.isActive && (st.group === 'special' || st.key === 'case_cancelled') && touched(st.key)) out.push({ kind: 'stage', stage: st });
    }
    return out;
  }, [cfg.stages, visited, cur]);

  const curIdx = nodes.findIndex((n) => n.kind === 'stage' && n.stage.key === cur);

  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-surface p-3 shadow-level-1">
      <ol className="flex min-w-max items-center gap-1">
        {nodes.map((n, i) => (
          <li key={n.kind === 'stage' ? n.stage.key : `choice:${n.group}`} className="flex items-center">
            {n.kind === 'choice' ? (
              <span
                title={`Either of: ${n.stages.map((x) => x.name).join(' · ')}`}
                className="flex items-center gap-1.5 rounded-full border border-dashed border-line px-2.5 py-1 text-xs font-medium text-muted"
              >
                <span>○</span>
                {STAGE_GROUP_LABELS[n.group] ?? n.group}
                <span className="rounded-full bg-surface-2 px-1.5 text-[10px]">1 of {n.stages.length}</span>
              </span>
            ) : (
              (() => {
                const st = n.stage;
                const isCur = st.key === cur;
                const seen = visited.has(st.key);
                const done = !isCur && seen && (i < curIdx || curIdx < 0);
                return (
                  <button
                    onClick={() => !isCur && onJump(st.key)}
                    title={seen ? `Visited · ${ws.stageVisits.filter((v) => v.stageKey === st.key).map((v) => hours((v.seconds ?? 0) / 3600)).join(', ')}` : st.name}
                    className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${isCur ? 'border-transparent text-white shadow-level-2' : done ? 'border-transparent bg-status-progress-bg text-status-progress-fg' : seen ? 'border-line bg-surface-2 text-ink' : 'border-line text-muted hover:bg-surface-2'}`}
                    style={isCur ? { background: st.color ?? '#0F172A' } : undefined}
                  >
                    <span>{isCur ? '⏳' : done ? '✓' : seen ? '•' : '○'}</span>
                    {st.name}
                  </button>
                );
              })()
            )}
            {i < nodes.length - 1 && <span className="mx-0.5 text-line">-</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

function Overview({ ws, onSaved }: { ws: WfCaseWorkspace; onSaved: () => void }) {
  const cfg = useWfConfig();
  const can = useCan();
  const pricing = useAlignerPricing();
  const c = ws.case;
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState<WfCasePatch>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const start = () => {
    setF({
      patientFirstName: c.patientFirstName, patientLastName: c.patientLastName, groupId: c.groupId, doctorName: c.doctorName, clinicName: c.clinicName, branch: c.branch,
      phone: c.phone, whatsapp: c.whatsapp, email: c.email, patientCode: c.patientCode, patientInfo: c.patientInfo, receivedThrough: c.receivedThrough, caseTypes: c.caseTypes,
      priority: c.priority, dueDate: c.dueDate, notes: c.notes, additionalNotes: c.additionalNotes, internalInstructions: c.internalInstructions, patientInstructions: c.patientInstructions,
      productionInstructions: c.productionInstructions, accountsInstructions: c.accountsInstructions, deliveryAddress: c.deliveryAddress, deliveryInstructions: c.deliveryInstructions,
      iprStatus: c.iprStatus, iprNotes: c.iprNotes, etchingStatus: c.etchingStatus, etchingNotes: c.etchingNotes, punching: c.punching, grade: c.grade, newOrRefinement: c.newOrRefinement, paymentStatus: c.paymentStatus,
      trays: c.trays,
      arch: c.arch,
      upperTrays: c.upperTrays,
      lowerTrays: c.lowerTrays,
      discountType: c.discountType,
      discountValue: c.discountValue,
    });
    setEdit(true);
  };
  const set = <K extends keyof WfCasePatch>(k: K, v: WfCasePatch[K]) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await wfApi.patch(c.id, f);
      setEdit(false);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const V = ({ label, children, mono = false }: { label: string; children: React.ReactNode; mono?: boolean }) => (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted">{label}</div>
      <div className={`text-sm text-ink ${mono ? 'font-mono' : ''}`}>{children ?? '-'}</div>
    </div>
  );
  const T = (k: keyof WfCasePatch, label: string, rows = 2, className = '') => (
    <Field label={label} className={className}>
      <textarea className={inputCls} rows={rows} value={(f[k] as string | null | undefined) ?? ''} onChange={(e) => set(k, (e.target.value || null) as never)} />
    </Field>
  );
  const I = (k: keyof WfCasePatch, label: string, type = 'text', mono = false) => (
    <Field label={label}>
      <input type={type} className={`${inputCls} ${mono ? 'font-mono uppercase' : ''}`} value={(f[k] as string | null | undefined) ?? ''} onChange={(e) => set(k, (e.target.value || null) as never)} />
    </Field>
  );

  if (edit) {
    return (
      <div className="space-y-4 rounded-lg border border-line bg-surface p-4 shadow-level-1">
        {err && <div className="text-sm text-status-urgent-fg">{err}</div>}
        <div className="grid grid-cols-1 gap-x-4 gap-y-3">
          {I('patientFirstName', 'First name')}
          {I('patientLastName', 'Last name')}
          {I('groupId', 'Group ID', 'text', true)}
          {I('doctorName', 'Doctor')}
          {I('clinicName', 'Clinic')}
          {I('branch', 'Branch')}
          {I('phone', 'Contact number')}
          {I('whatsapp', 'WhatsApp')}
          {I('email', 'Email')}
          {I('patientCode', 'Patient ID')}
          <Field label="Received through">
            <select className={inputCls} value={f.receivedThrough ?? ''} onChange={(e) => set('receivedThrough', e.target.value || null)}>
              <option value="">-</option>
              {cfg.optionsOf('received_through', true).map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Delivery">
            <TurnaroundPicker
              value={f.priority ?? 'normal'}
              receivedAt={ws.case.receivedAt}
              onChange={(p) => {
                // Keep a date somebody agreed by hand; otherwise follow the new promise.
                const keep = !isDerivedDueDate(f.dueDate, ws.case.receivedAt, f.priority ?? 'normal');
                setF((cur) => ({
                  ...cur,
                  priority: p,
                  dueDate: keep ? cur.dueDate : turnaroundDueDate(ws.case.receivedAt, p),
                }));
              }}
            />
          </Field>
          {I('dueDate', 'Due date', 'date')}
          <Field label="IPR"><select className={inputCls} value={f.iprStatus ?? 'not_required'} onChange={(e) => set('iprStatus', e.target.value as WfCase['iprStatus'])}>{WF_REQ_STATUSES.map((s) => <option key={s} value={s}>{WF_REQ_LABELS[s]}</option>)}</select></Field>
          <Field label="Etching guide"><select className={inputCls} value={f.etchingStatus ?? 'not_required'} onChange={(e) => set('etchingStatus', e.target.value as WfCase['etchingStatus'])}>{WF_REQ_STATUSES.map((s) => <option key={s} value={s}>{WF_REQ_LABELS[s]}</option>)}</select></Field>
          {I('punching', 'Punching')}
          {I('grade', 'Grade')}
          <Field label="New / refinement">
            <select className={inputCls} value={f.newOrRefinement ?? ''} onChange={(e) => set('newOrRefinement', (e.target.value || null) as never)}><option value="">-</option><option value="new">New case</option><option value="refinement">Refinement</option></select>
          </Field>
          {I('paymentStatus', 'Payment status')}
          <Field label="Aligners - upper / lower" hint="The total and the models follow from these two numbers." className="md:col-span-2">
            <ArchTrayInputs
              upper={f.upperTrays == null ? '' : String(f.upperTrays)}
              lower={f.lowerTrays == null ? '' : String(f.lowerTrays)}
              onChange={(u, l) => {
                const nu = u === '' ? null : Math.max(0, parseInt(u, 10) || 0);
                const nl = l === '' ? null : Math.max(0, parseInt(l, 10) || 0);
                setF((p) => ({ ...p, upperTrays: nu, lowerTrays: nl, trays: ((nu ?? 0) + (nl ?? 0)) || (p.trays ?? null) }));
              }}
            />
          </Field>
          <Field label="Discount" hint="A percentage or PKR amount off the aligner price. Changing it re-issues the invoice.">
            <DiscountInput
              value={alignerDiscountOf(f)}
              onChange={(d) => {
                set('discountType', d?.type ?? null);
                set('discountValue', d?.value ?? null);
              }}
            />
            {typeof f.trays === 'number' && f.trays > 0 && (
              <div className="mt-1 text-xs text-muted">
                Clinic price: <QuoteLine quote={alignerQuote(f.trays, pricing, alignerDiscountOf(f))} />
                {ws.invoice && (
                  <div>
                    Invoiced ({ws.invoice.invoiceNumber}): <b className="text-ink">PKR {(ws.invoice.totalPaisa / 100).toLocaleString()}</b>
                  </div>
                )}
              </div>
            )}
          </Field>
        </div>
        <div>
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted">Case types</div>
          <div className="flex flex-wrap gap-1.5">
            {cfg.optionsOf('case_type', true).map((o) => {
              const on = (f.caseTypes ?? []).includes(o.key);
              return <button type="button" key={o.key} onClick={() => set('caseTypes', on ? (f.caseTypes ?? []).filter((t) => t !== o.key) : [...(f.caseTypes ?? []), o.key])} className={`rounded-full border px-2.5 py-1 text-xs font-medium ${on ? 'border-primary bg-primary text-primary-fg' : 'border-line text-ink hover:bg-surface-2'}`}>{o.label}</button>;
            })}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-x-4 gap-y-3">
          {T('notes', 'Notes')}
          {T('additionalNotes', 'Additional notes')}
          {T('internalInstructions', 'Internal instructions')}
          {T('productionInstructions', 'Production instructions')}
          {T('patientInstructions', 'Patient instructions')}
          {T('accountsInstructions', 'Accounts instructions')}
          {T('iprNotes', 'IPR details')}
          {T('etchingNotes', 'Etching guide details')}
          {T('deliveryAddress', 'Delivery address')}
          {T('deliveryInstructions', 'Delivery instructions')}
          {T('patientInfo', 'Additional patient information', 2, 'md:col-span-2')}
        </div>
        <div className="flex justify-end gap-2">
          <Btn tone="ghost" onClick={() => setEdit(false)}>Cancel</Btn>
          <Btn tone="accent" disabled={busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save changes'}</Btn>
        </div>
      </div>
    );
  }

  return (
    <>
    <MobileOverview ws={ws} onEdit={can('workflow:update') ? start : undefined} />
    <div className="hidden grid-cols-1 gap-4 lg:grid lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        {PRODUCTION_COUNT_STAGES.includes(c.stageKey) && (
          <Panel title="Production - upper / lower aligners" action={<ProformaPrintButton caseId={c.id} label="Print proforma" />}>
            <ProductionCounters c={c} onChanged={onSaved} compact />
          </Panel>
        )}
        <Panel title="Patient & doctor" action={can('workflow:update') ? <Btn tone="ghost" onClick={start}>Edit</Btn> : null}>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <V label="Patient">{c.patientName}</V>
            <V label="Patient ID">{c.patientCode}</V>
            <V label="Contact">{c.phone}</V>
            <V label="WhatsApp">{c.whatsapp}</V>
            <V label="Email">{c.email}</V>
            <V label="Group ID" mono>{c.groupId}</V>
            <V label="Doctor">{c.doctorName}</V>
            <V label="Clinic">{c.clinicName}{c.branch ? ` · ${c.branch}` : ''}</V>
            <V label="Received through">{c.receivedThroughRaw ?? cfg.optionLabel('received_through', c.receivedThrough)}</V>
            <V label="Case type(s)">{c.caseTypeRaw ? `${c.caseTypeRaw} ` : ''}{c.caseTypes.map((t) => cfg.optionLabel('case_type', t)).join(', ')}</V>
            {typeof c.trays === 'number' && c.trays > 0 && (
              <V label="Aligners">
                {alignerSummary(c)} · <span className="text-muted">{ws.invoice ? `PKR ${(ws.invoice.totalPaisa / 100).toLocaleString()} to clinic (${ws.invoice.invoiceNumber})` : <>PKR {alignerQuote(c.trays, pricing, alignerDiscountOf(c)).netRupees.toLocaleString()} to clinic{alignerDiscountOf(c) ? ` (after ${alignerDiscountOf(c)!.type === 'percent' ? `${c.discountValue}%` : `PKR ${c.discountValue!.toLocaleString()}`} discount)` : ''}</>}</span>
                {c.currentTray ? <span className="text-muted"> · on tray {c.currentTray}</span> : null}
              </V>
            )}
            <div className="col-span-full">
              <PatientAccessCard caseId={c.id} />
            </div>
            <V label="New / refinement">{c.newOrRefinement}</V>
            <V label="Grade / punching">{[c.grade, c.punching].filter(Boolean).join(' / ') || null}</V>
            <V label="Payment">{c.paymentStatus}</V>
            <V label="Created">{fmtDT(c.createdAt)} {c.source === 'import' && <span className="text-xs text-muted">(imported)</span>}</V>
            <V label="Additional info">{c.patientInfo}</V>
          </div>
        </Panel>
        <Panel title="Notes & instructions (case-level)">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <V label="Notes">{c.notes}</V>
            <V label="Additional notes">{c.additionalNotes}</V>
            <V label="Internal instructions">{c.internalInstructions}</V>
            <V label="Production instructions">{c.productionInstructions}</V>
            <V label="Patient instructions">{c.patientInstructions}</V>
            <V label="Accounts instructions">{c.accountsInstructions}</V>
            <V label="Delivery">{[c.deliveryAddress, c.deliveryInstructions].filter(Boolean).join(' - ') || null}</V>
            <V label="IPR / etching details">{[c.iprNotes && `IPR: ${c.iprNotes}`, c.etchingNotes && `Etching: ${c.etchingNotes}`].filter(Boolean).join(' · ') || null}</V>
          </div>
        </Panel>
        {ws.stage?.checklist.length ? (
          <Panel title={`${ws.stage.name} checklist`}>
            <ChecklistEditor ws={ws} onSaved={onSaved} />
          </Panel>
        ) : null}
      </div>
      <div className="space-y-4">
        <Panel title="Stage history">
          <ul className="space-y-1 text-sm">
            {ws.stageVisits.map((v) => (
              <li key={v.id} className="flex items-center justify-between gap-2">
                <StagePill stageKey={v.stageKey} small />
                <span className="font-mono text-xs text-muted">{v.exitedAt ? hours((v.seconds ?? 0) / 3600) : `${hours(ws.case.hoursInStage)} (now)`}</span>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title="Other cases for this patient">
          {ws.patientCases.length ? (
            <ul className="space-y-1 text-sm">
              {ws.patientCases.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2">
                  <Link to={`/workflow/cases/${p.id}`} className="font-mono text-xs hover:underline">{p.caseNumber}</Link>
                  <span className="text-xs">{cfg.optionLabel('case_type', p.caseType)}</span>
                  <StagePill stageKey={p.stageKey} small />
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-xs text-muted">None</div>
          )}
        </Panel>
        {ws.holds.length > 0 && (
          <Panel title="Holds">
            <ul className="space-y-2 text-xs">
              {ws.holds.map((h) => (
                <li key={h.id} className="rounded border border-line p-2">
                  <div className="font-semibold">{cfg.optionLabel('hold_reason', h.reason)} {h.resolvedAt ? <span className="text-status-progress-fg">· resolved {fmtDT(h.resolvedAt)}</span> : <span className="text-amber-700">· open</span>}</div>
                  <div className="text-muted">Placed {fmtDT(h.placedAt)} by {h.placedByName ?? '-'} · responsible {h.responsibleName ?? '-'} · expected {fmtD(h.expectedAt)}</div>
                  {h.notes && <div>{h.notes}</div>}
                </li>
              ))}
            </ul>
          </Panel>
        )}
      </div>
    </div>
    </>
  );
}

function ChecklistEditor({ ws, onSaved }: { ws: WfCaseWorkspace; onSaved: () => void }) {
  const items = ws.stage?.checklist ?? [];
  const [state, setState] = useState<Record<string, boolean>>(ws.case.checklistState);
  useEffect(() => setState(ws.case.checklistState), [ws.case.checklistState]);
  const done = items.filter((i) => state[i]).length;
  const save = async (next: Record<string, boolean>) => {
    setState(next);
    await wfApi.patch(ws.case.id, { checklistState: next });
    onSaved();
  };
  return (
    <div>
      <div className="mb-2 h-1.5 w-full rounded bg-surface-2"><div className="h-1.5 rounded bg-accent" style={{ width: `${items.length ? (done / items.length) * 100 : 0}%` }} /></div>
      {items.map((i) => (
        <label key={i} className="flex items-center gap-2 py-0.5 text-sm">
          <input type="checkbox" checked={!!state[i]} onChange={(e) => void save({ ...state, [i]: e.target.checked })} /> {i}
        </label>
      ))}
      <div className="mt-1 text-xs text-muted">{done}/{items.length} complete - required before leaving this stage.</div>
    </div>
  );
}

function Panel({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

const EVENT_ICON: Record<string, string> = { created: '➕', imported: '📥', stage_change: '➡️', field_change: '✏️', assigned: '👤', note: '📝', file: '📎', approval: '✅', review: '🩺', hold: '⏸', resume: '▶️', cancel: '⛔', reopen: '♻️', setup_version: '🧩', production: '🖨', dispatch: '🚚', rework: '🔧', reprint: '🔁', checklist: '☑️', bulk: '📦' };

function Timeline({ ws }: { ws: WfCaseWorkspace }) {
  const cfg = useWfConfig();
  const can = useCan();
  const [filter, setFilter] = useState<string>('');
  const events = [...ws.events].reverse().filter((e) => !filter || e.eventType === filter);
  const types = [...new Set(ws.events.map((e) => e.eventType))];
  const describe = (e: WfEvent) => {
    if (e.eventType === 'stage_change' || (e.fromStage && e.toStage && e.fromStage !== e.toStage)) return <>Status changed from <b>{cfg.stageName(e.fromStage)}</b> to <b>{cfg.stageName(e.toStage)}</b></>;
    if (e.eventType === 'created') return <>Case received → <b>{cfg.stageName(e.toStage)}</b></>;
    return <>{e.eventType.replace(/_/g, ' ')}</>;
  };
  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select className={`${inputCls} w-48`} value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All events</option>
          {types.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        {can('workflow:export') && <a className="ml-auto rounded border border-line px-3 py-1.5 text-sm hover:bg-surface-2" href={historyExportUrl(ws.case.id)}>Export history (Excel)</a>}
      </div>
      <ol className="relative border-l border-line pl-5">
        {events.map((e) => (
          <li key={e.id} className="mb-4">
            <span className="absolute -left-2.5 grid h-5 w-5 place-items-center rounded-full bg-surface text-xs">{EVENT_ICON[e.eventType] ?? '•'}</span>
            <div className="text-xs text-muted">{fmtDT(e.at)} - <b className="text-ink">{e.actorName ?? 'System'}</b></div>
            <div className="text-sm text-ink">{describe(e)}</div>
            {e.comment && e.eventType !== 'created' && <div className="mt-0.5 whitespace-pre-wrap text-sm text-ink/80">{e.comment}</div>}
            {e.data && Object.keys(e.data).length > 0 && e.eventType !== 'field_change' && (
              <details className="mt-0.5 text-xs text-muted"><summary className="cursor-pointer">details</summary><pre className="whitespace-pre-wrap">{JSON.stringify(e.data, null, 1)}</pre></details>
            )}
            {e.eventType === 'field_change' && e.data && (
              <div className="mt-0.5 text-xs text-muted">{((e.data.changed as string[]) ?? []).map((k) => <span key={k} className="mr-2">{k}: <s>{String((e.data!.before as Record<string, unknown>)?.[k] ?? '-')}</s> → <b>{String((e.data!.after as Record<string, unknown>)?.[k] ?? '-')}</b></span>)}</div>
            )}
          </li>
        ))}
      </ol>
      {!events.length && <Empty>No events</Empty>}
    </div>
  );
}

function Notes({ ws, onChange, onAdd }: { ws: WfCaseWorkspace; onChange: () => void; onAdd: () => void }) {
  const [kind, setKind] = useState('');
  const notes = ws.notes.filter((n) => !kind || n.kind === kind);
  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          <button onClick={() => setKind('')} className={`rounded-full px-2.5 py-1 text-xs ${!kind ? 'bg-primary text-primary-fg' : 'border border-line'}`}>All</button>
          {WF_NOTE_KINDS.map((k) => <button key={k} onClick={() => setKind(k)} className={`rounded-full px-2.5 py-1 text-xs capitalize ${kind === k ? 'bg-primary text-primary-fg' : 'border border-line'}`}>{k} ({ws.notes.filter((n) => n.kind === k).length})</button>)}
        </div>
        <Btn tone="accent" className="ml-auto" onClick={onAdd}>+ Add note</Btn>
      </div>
      <ul className="space-y-2">
        {notes.map((n) => (
          <li key={n.id} className={`rounded border p-3 ${n.pinned ? 'border-accent bg-accent/5' : 'border-line'}`}>
            <div className="flex items-center justify-between text-xs text-muted">
              <span><span className="rounded bg-surface-2 px-1.5 py-0.5 capitalize text-ink">{n.kind}</span> · {n.createdByName ?? '-'} · {fmtDT(n.createdAt)}</span>
              <button className="hover:text-ink" onClick={() => wfApi.pinNote(ws.case.id, n.id, !n.pinned).then(onChange)}>{n.pinned ? 'Unpin' : 'Pin'}</button>
            </div>
            <div className="mt-1 whitespace-pre-wrap text-sm text-ink">{n.body}</div>
          </li>
        ))}
      </ul>
      {!notes.length && <Empty>No notes yet - notes are timestamped and never overwrite each other.</Empty>}
    </div>
  );
}

/** Categories worth putting in front of a doctor. A raw scan or STL is material, not a review item. */
const SHAREABLE_CATEGORIES = new Set(['video', 'setup', 'treatment_plan', 'screenshot', 'image', 'pdf']);

function Files({ ws, onAdd, onChanged }: { ws: WfCaseWorkspace; onAdd: () => void; onChanged: () => void }) {
  const can = useCan();
  const [cat, setCat] = useState('');
  const [view3d, setView3d] = useState<{ id: string; name: string } | null>(null);
  const [sharing, setSharing] = useState<string | null>(null);
  const [shareErr, setShareErr] = useState<string | null>(null);
  const [confirmFile, setConfirmFile] = useState<WfFile | null>(null);
  const [deleting, setDeleting] = useState(false);
  const files = ws.files.filter((f) => !cat || f.category === cat);
  const cats = [...new Set(ws.files.map((f) => f.category))];
  const shared = ws.files.find((f) => f.sharedForApproval) ?? null;
  const hasTrays = !!ws.case.trays && ws.case.trays > 0;

  const share = async (fileId: string, on: boolean) => {
    setSharing(fileId);
    setShareErr(null);
    try {
      await wfApi.shareForApproval(ws.case.id, fileId, on, null);
      onChanged();
    } catch (e) {
      setShareErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSharing(null);
    }
  };

  const removeFile = async () => {
    if (!confirmFile) return;
    setDeleting(true);
    setShareErr(null);
    try {
      await wfApi.deleteFile(ws.case.id, confirmFile.id);
      setConfirmFile(null);
      onChanged();
    } catch (e) {
      setShareErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
      {/* What the doctor is currently being asked to approve, stated before the file list. */}
      <div className="mb-3 rounded-md border border-line bg-surface-2 px-3 py-2 text-sm">
        {shared ? (
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-semibold text-ink">Shared with the doctor for approval:</span>
            <span className="min-w-0 truncate">{shared.originalName}</span>
            <button
              type="button"
              disabled={sharing === shared.id}
              onClick={() => void share(shared.id, false)}
              className="rounded border border-line bg-surface px-2 py-0.5 text-xs hover:bg-surface-2 disabled:opacity-50"
            >
              {sharing === shared.id ? 'Withdrawing…' : 'Withdraw'}
            </button>
          </span>
        ) : (
          <span className="text-muted">
            Nothing is shared for approval yet. Upload the model video, then press{' '}
            <b className="text-ink">Share for approval</b> on its row - the doctor gets it playing in
            their portal.
            {!hasTrays && ' The number of aligners has to be set first.'}
          </span>
        )}
      </div>
      {shareErr && <div className="mb-2 text-sm text-status-urgent-fg">{shareErr}</div>}
      {!hasTrays && (
        <div className="mb-3">
          <TrayCountRequired caseId={ws.case.id} onSet={onChanged} priority={ws.case.priority} />
        </div>
      )}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select className={`${inputCls} w-48`} value={cat} onChange={(e) => setCat(e.target.value)}><option value="">All categories</option>{cats.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}</select>
        <Btn tone="accent" className="ml-auto" onClick={onAdd}>+ Upload</Btn>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-[11px] uppercase tracking-wider text-muted"><tr><th className="py-1">File</th><th>Category</th><th>Version</th><th>Size</th><th>Uploaded by</th><th>Date</th><th>Description</th><th></th></tr></thead>
        <tbody>
          {files.map((f) => (
            <tr key={f.id} className={`border-t border-line ${f.sharedForApproval ? 'bg-accent/5' : ''}`}>
              <td className="py-1.5 font-medium">
                {f.originalName}
                {f.sharedForApproval && (
                  <span className="ml-2 rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-accent-fg">
                    With doctor
                  </span>
                )}
              </td>
              <td className="capitalize">{f.category.replace(/_/g, ' ')}</td>
              <td className="font-mono text-xs">v{f.version}{f.replacesFileId ? ' ↺' : ''}</td>
              <td className="text-xs">{formatFileSize(f.sizeBytes)}</td>
              <td className="text-xs">{f.uploadedByName ?? '-'}</td>
              <td className="text-xs">{fmtDT(f.uploadedAt)}</td>
              <td className="text-xs">{f.description}</td>
              <td className="text-right">{isModelFile(f.originalName) && <button type="button" className="mr-1 rounded border border-primary bg-primary px-2 py-0.5 text-xs font-medium text-primary-fg hover:opacity-90" onClick={() => setView3d({ id: f.id, name: f.originalName })}>View 3D</button>}<a className="rounded border border-line px-2 py-0.5 text-xs hover:bg-surface-2" href={fileDownloadUrl(ws.case.id, f.id)}>Download</a> {(f.category === 'image' || f.category === 'pdf' || f.category === 'video' || f.category === 'screenshot') && <a className="rounded border border-line px-2 py-0.5 text-xs hover:bg-surface-2" href={fileDownloadUrl(ws.case.id, f.id, true)} target="_blank" rel="noreferrer">View</a>}{can('workflow:update') && !f.sharedForApproval && (
                  <button
                    type="button"
                    onClick={() => setConfirmFile(f)}
                    className="ml-1 rounded border border-status-urgent-fg/30 px-2 py-0.5 text-xs font-medium text-status-urgent-fg hover:bg-status-urgent-bg"
                    title={`Delete ${f.originalName}`}
                  >
                    Delete
                  </button>
                )}{SHAREABLE_CATEGORIES.has(f.category) && !f.sharedForApproval && (
                  <button
                    type="button"
                    disabled={sharing === f.id || !hasTrays}
                    title={hasTrays ? undefined : 'Set the number of aligners first'}
                    onClick={() => void share(f.id, true)}
                    className="ml-1 rounded border border-accent bg-accent px-2 py-0.5 text-xs font-medium text-accent-fg hover:opacity-90 disabled:opacity-50"
                  >
                    {sharing === f.id ? 'Sharing…' : shared ? 'Share instead' : 'Share for approval'}
                  </button>
                )}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!files.length && <Empty>No files. STL, scans, images, X-rays, PDFs, videos, setups, approvals - all versions are kept.</Empty>}
      <Modal
        open={!!confirmFile}
        onClose={() => !deleting && setConfirmFile(null)}
        title="Delete this file?"
        footer={
          <>
            <Btn tone="ghost" disabled={deleting} onClick={() => setConfirmFile(null)}>
              No, keep it
            </Btn>
            <Btn tone="danger" disabled={deleting} onClick={() => void removeFile()}>
              {deleting ? 'Deleting…' : 'Yes, delete'}
            </Btn>
          </>
        }
      >
        <p className="text-sm text-ink">{confirmFile?.originalName}</p>
        <p className="mt-2 text-sm leading-relaxed text-muted">
          It leaves the case and the clinic stops seeing it. The file itself is kept, so a
          super-admin can put it back - worth knowing for a scan, which is often the only copy
          anybody has.
        </p>
      </Modal>

      {view3d && (
        <Suspense fallback={<div className="fixed inset-0 z-modal grid place-items-center bg-black/85 text-sm text-white/70">Loading viewer…</div>}>
          <StlViewer url={fileDownloadUrl(ws.case.id, view3d.id, true)} name={view3d.name} onClose={() => setView3d(null)} />
        </Suspense>
      )}
    </div>
  );
}

function Approvals({ ws, act, onChange }: { ws: WfCaseWorkspace; act: (a: WfAction) => void; onChange: () => void }) {
  const cfg = useWfConfig();
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <Panel title="Setup / simulation versions" action={<Btn tone="ghost" onClick={() => act('setup')}>+ Version</Btn>}>
        <ul className="space-y-2 text-sm">
          {ws.setupVersions.map((v) => (
            <li key={v.id} className="rounded border border-line p-2">
              <div className="flex items-center justify-between"><b>V{v.version} · {v.kind.replace(/_/g, ' ')}</b><select className="rounded border border-line bg-surface px-1 text-xs" value={v.status} onChange={(e) => wfApi.setSetupStatus(ws.case.id, v.id, e.target.value).then(onChange)}>{['draft', 'shared', 'doctor_review', 'approved', 'rejected', 'superseded'].map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}</select></div>
              <div className="text-xs text-muted">{v.createdByName ?? '-'} · {fmtDT(v.createdAt)}</div>
              {v.notes && <div className="text-xs">{v.notes}</div>}
              {/* A version without media is a note to ourselves; with media it is what the doctor sees. */}
              {v.fileId ? (
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                  <a className="text-accent underline" href={fileDownloadUrl(ws.case.id, v.fileId, true)} target="_blank" rel="noreferrer">
                    View media
                  </a>
                  {ws.files.find((f) => f.id === v.fileId)?.sharedForApproval ? (
                    <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-accent-fg">
                      With doctor
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => wfApi.shareForApproval(ws.case.id, v.fileId!, true, v.notes).then(onChange)}
                      className="rounded border border-accent px-2 py-0.5 text-[11px] font-medium text-accent hover:bg-accent/10"
                    >
                      Share with doctor
                    </button>
                  )}
                </div>
              ) : (
                <div className="mt-1 text-[11px] text-status-warn-fg">No file attached - the doctor has nothing to review.</div>
              )}
            </li>
          ))}
          {!ws.setupVersions.length && <li className="text-xs text-muted">No setup versions yet.</li>}
        </ul>
      </Panel>
      <Panel title="Doctor &amp; production approvals" action={<div className="flex gap-1"><Btn tone="ghost" onClick={() => act('request_approval')}>Request</Btn><Btn tone="ghost" onClick={() => act('approve')}>Decide</Btn></div>}>
        <ul className="space-y-2 text-sm">
          {ws.approvals.map((a) => (
            <li key={a.id} className="rounded border border-line p-2">
              <div className="flex items-center justify-between"><b>{WF_APPROVAL_KIND_LABELS[a.kind] ?? a.kind}: {WF_APPROVAL_LABELS[a.status]}</b><span className="text-xs text-muted">{a.method ? cfg.optionLabel('approval_method', a.method) : ''}</span></div>
              <div className="text-xs text-muted">{a.decidedAt ? `Decided ${fmtDT(a.decidedAt)} by ${a.decidedByName ?? '-'}` : `Requested ${fmtDT(a.requestedAt)} by ${a.requestedByName ?? '-'}`}</div>
              {a.reason && <div className="text-xs text-status-urgent-fg">Reason: {a.reason}</div>}
              {a.comments && <div className="text-xs">{a.comments}</div>}
              {a.returnToStage && <div className="text-xs">Returned to {cfg.stageName(a.returnToStage)}</div>}
              {a.evidenceFileId && <a className="text-xs text-accent underline" href={fileDownloadUrl(ws.case.id, a.evidenceFileId)}>Approval evidence</a>}
            </li>
          ))}
          {!ws.approvals.length && <li className="text-xs text-muted">No approvals recorded.</li>}
        </ul>
      </Panel>
      <Panel title="Doctor reviews" action={<Btn tone="ghost" onClick={() => act('review')}>Review</Btn>}>
        <ul className="space-y-2 text-sm">
          {ws.reviews.map((r) => (
            <li key={r.id} className="rounded border border-line p-2">
              <div className="flex items-center justify-between"><b>{r.queue === 'dr_ali' ? 'Dr Ali' : 'Mubeen / Awais'} - {r.decision.replace(/_/g, ' ')}</b></div>
              <div className="text-xs text-muted">{r.reviewerName ?? '-'} · {fmtDT(r.at)}{r.nextStage ? ` → ${cfg.stageName(r.nextStage)}` : ''}</div>
              {r.comment && <div className="text-xs">{r.comment}</div>}
              {r.instructions && <div className="text-xs italic">Instructions: {r.instructions}</div>}
            </li>
          ))}
          {!ws.reviews.length && <li className="text-xs text-muted">No reviews recorded.</li>}
        </ul>
      </Panel>
    </div>
  );
}

function Production({ ws, act, onChange }: { ws: WfCaseWorkspace; act: (a: WfAction, extra?: Partial<ActionTarget>) => void; onChange: () => void }) {
  const cfg = useWfConfig();
  const c = ws.case;
  const kinds: Array<[string, string]> = [['print_order', 'Print order'], ['print_run', 'Print run'], ['print_qc', 'Print QC'], ['touch_up', 'Final touch up'], ['finishing', 'Finishing'], ['qc', 'QC'], ['packaging', 'Packaging'], ['reprint', 'Reprint'], ['rework', 'Rework']];
  const complete = async (id: string) => {
    await wfApi.patchProduction(c.id, id, { status: 'completed' });
    onChange();
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-[20rem] flex-1"><ProductionCounters c={c} onChanged={onChange} /></div>
        <div className="rounded-lg border border-line bg-surface p-4 text-xs text-muted shadow-level-1">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">Case proforma</div>
          <p className="mb-2 max-w-[18rem]">{alignerSummary(c) ?? 'Aligner numbers not set yet.'} The proforma goes in the box with the aligners at Packaging.</p>
          <ProformaPrintButton caseId={c.id} />
        </div>
      </div>
      <div className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">Production instructions</div>
          <div className="w-full whitespace-pre-wrap text-sm">{c.productionInstructions ?? <span className="text-muted">- none - (edit in Overview)</span>}</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <ReqBadge label="IPR" s={c.iprStatus} />
            <ReqBadge label="Etching guide" s={c.etchingStatus} />
            {c.punching && <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[11px]">Punching: {c.punching}</span>}
            <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[11px]">Print version {c.printVersion}</span>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {kinds.slice(0, 7).map(([k, l]) => <Btn key={k} tone="ghost" onClick={() => act('production', { productionKind: k })}>+ {l}</Btn>)}
          <Btn tone="danger" onClick={() => act('reprint')}>Reprint required</Btn>
          <Btn tone="danger" onClick={() => act('rework')}>Rework required</Btn>
        </div>
      </div>
      <div className="rounded-lg border border-line bg-surface p-4 shadow-level-1">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-muted"><tr><th className="py-1">Record</th><th>Status</th><th>Details</th><th>Checklist</th><th>Staff</th><th>Started</th><th>Completed</th><th></th></tr></thead>
          <tbody>
            {ws.production.map((p) => (
              <tr key={p.id} className="border-t border-line align-top">
                <td className="py-1.5 font-medium">{kinds.find((k) => k[0] === p.kind)?.[1] ?? p.kind} <span className="font-mono text-xs text-muted">v{p.version}</span></td>
                <td><span className={`rounded px-1.5 py-0.5 text-xs ${p.status === 'completed' ? 'bg-status-progress-bg text-status-progress-fg' : p.status === 'failed' ? 'bg-status-urgent-bg text-status-urgent-fg' : 'bg-surface-2'}`}>{p.status.replace('_', ' ')}</span></td>
                <td className="text-xs">{Object.entries(p.data).filter(([, v]) => v !== '' && v != null).map(([k, v]) => <div key={k}><span className="text-muted">{k}:</span> {String(v)}</div>)}{p.notes && <div className="italic">{p.notes}</div>}</td>
                <td className="text-xs">{p.checklist.length ? `${p.checklist.filter((i) => i.done).length}/${p.checklist.length}` : ''}</td>
                <td className="text-xs">{p.staffName ?? '-'}</td>
                <td className="text-xs">{fmtDT(p.startedAt)}</td>
                <td className="text-xs">{fmtDT(p.completedAt)}</td>
                <td className="text-right">{p.status !== 'completed' && p.status !== 'failed' && <button className="rounded border border-line px-2 py-0.5 text-xs hover:bg-surface-2" onClick={() => void complete(p.id)}>Mark complete</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!ws.production.length && <Empty>No production records yet.</Empty>}
      </div>
      <div className="text-xs text-muted">Current stage: {cfg.stageName(c.stageKey)}. Reprint / rework return the case to the right stage while keeping every earlier record.</div>
    </div>
  );
}

function Dispatch({ ws, act }: { ws: WfCaseWorkspace; act: (a: WfAction) => void }) {
  const cfg = useWfConfig();
  const d = ws.dispatch;
  const c = ws.case;
  const packaging = ws.production.filter((p) => p.kind === 'packaging');
  const V = ({ label, v }: { label: string; v: React.ReactNode }) => <div><div className="text-[11px] font-semibold uppercase tracking-wider text-muted">{label}</div><div className="text-sm">{v ?? '-'}</div></div>;
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Panel title="Dispatch checklist">
        <div className="grid grid-cols-2 gap-3">
          <V label="Patient" v={c.patientName} />
          <V label="Group ID / clinic" v={`${c.groupId ?? '-'} · ${c.clinicName ?? c.doctorName ?? '-'}`} />
          <V label="Case type" v={c.caseTypeRaw ?? c.caseTypes.map((t) => cfg.optionLabel('case_type', t)).join(', ')} />
          <V label="Contact" v={c.phone ?? c.whatsapp} />
          <V label="Delivery address" v={c.deliveryAddress} />
          <V label="Due date" v={fmtD(c.dueDate)} />
          <V label="Aligners" v={alignerSummary(c)} />
          <V label="Packaging" v={packaging.some((p) => p.status === 'completed') ? <span className="text-status-progress-fg">Completed</span> : packaging.length ? 'In progress' : 'Not recorded'} />
          <V label="Payment status" v={c.paymentStatus} />
          <V label="Delivery instructions" v={c.deliveryInstructions} />
          <V label="Stage" v={cfg.stageName(c.stageKey)} />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <ProformaPrintButton caseId={c.id} label="Print proforma for the box" />
          {c.stageKey === R.packaging && <Btn tone="accent" onClick={() => act('dispatch')}>Dispatch now</Btn>}
          {c.stageKey === 'dispatched' && <Btn tone="ghost" onClick={() => act('dispatch')}>Update tracking</Btn>}
          {c.stageKey !== R.packaging && c.stageKey !== R.dispatch && <span className="text-xs text-muted">Move the case to Packaging (its checklist must be complete) to enable dispatch.</span>}
        </div>
      </Panel>
      <Panel title="Dispatch record">
        {d ? (
          <div className="grid grid-cols-2 gap-3">
            <V label="Dispatched at" v={fmtDT(d.dispatchedAt)} />
            <V label="Courier" v={cfg.optionLabel('courier', d.courier)} />
            <V label="Tracking number" v={<span className="font-mono">{d.trackingNumber ?? '-'}</span>} />
            <V label="Receiver" v={d.receiver} />
            <V label="Address" v={d.address} />
            <V label="Dispatch staff" v={d.staffName} />
            <V label="Package contents" v={d.contents} />
            <V label="Notes" v={d.notes} />
          </div>
        ) : (
          <Empty>Not dispatched yet.</Empty>
        )}
      </Panel>
    </div>
  );
}


// ---------------------------------------------------------------------------------------------
// Phone layout (mirrors the "Lab OS" case screen). Everything below is lg:hidden.
// ---------------------------------------------------------------------------------------------

/** Approval as the server counts it for the Printing gate (service.ts validateTransition). */
function approvalGranted(c: WfCase): boolean {
  return c.approvalState === 'approved' || c.approvalState === 'approved_with_instructions' || c.productionApproved;
}

/**
 * True while an unapproved case sits between the approval stage and Printing: the one situation
 * where "Move stage" will be refused and the reason is not visible on the screen.
 */
function approvalHoldsPrinting(c: WfCase, visited: Set<string>, path: WfStage[], stepIdx: number): boolean {
  if (approvalGranted(c)) return false;
  if (!visited.has(R.doctorApproval) && c.stageKey !== R.doctorApproval) return false;
  const printingIdx = path.findIndex((s) => s.key === R.printing);
  return printingIdx === -1 || stepIdx < printingIdx;
}

function ageLabel(iso: string): string {
  const h = (Date.now() - Date.parse(iso)) / 3_600_000;
  if (h < 1) return 'just now';
  if (h < 24) return `${Math.round(h)}h ago`;
  return fmtD(iso);
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';
}

function dueChip(c: WfCase): { text: string; tone: 'bad' | 'warn' | 'ok' } | null {
  if (!c.dueDate) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(c.dueDate + 'T00:00:00');
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  if (days < 0) return { text: `Overdue ${-days}d`, tone: 'bad' };
  if (days === 0) return { text: 'Due today', tone: 'warn' };
  if (days === 1) return { text: 'Due tomorrow', tone: 'warn' };
  return { text: `Due in ${days}d`, tone: 'ok' };
}

function MobileCaseHeader({ ws, act, canMove, onDelete }: { ws: WfCaseWorkspace; act: (a: WfAction, extra?: Partial<ActionTarget>) => void; canMove: boolean; onDelete: () => void }) {
  const nav = useNavigate();
  const cfg = useWfConfig();
  const can = useCan();
  const c = ws.case;
  const stage = ws.stage;
  const due = dueChip(c);
  const path = cfg.stages.filter((st) => st.isActive && st.group !== 'special' && st.key !== 'case_cancelled');
  const stepIdx = path.findIndex((st) => st.key === c.stageKey);
  const visited = new Set(ws.stageVisits.map((v) => v.stageKey));
  const urgent = c.priority === 'urgent' || c.priority === 'emergency';

  return (
    <div className="-mx-3 -mt-3 lg:hidden">
      {/* App bar */}
      <div className="sticky top-[var(--wf-toolbar-h,0px)] z-sticky flex items-center gap-2 border-b border-line bg-surface px-2 py-2">
        <button onClick={() => nav(-1)} aria-label="Back" className="tap grid h-10 w-10 place-items-center rounded-full text-ink">‹</button>
        <span className="font-mono text-base font-bold text-ink">{c.caseNumber}</span>
        {urgent && <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-bold uppercase text-amber-800">{WF_PRIORITY_LABELS[c.priority]}</span>}
        <span className="flex-1" />
        <button onClick={() => nav('/workflow/cases')} aria-label="Search" className="tap grid h-10 w-10 place-items-center rounded-full text-ink">🔍</button>
        <Link to="/workflow/inbox" aria-label="Notifications" className="tap grid h-10 w-10 place-items-center rounded-full text-ink">🔔</Link>
        <button onClick={() => act('note')} aria-label="More" className="tap grid h-10 w-10 place-items-center rounded-full text-ink">⋯</button>
      </div>

      {/* Hero */}
      <div className="border-b border-line bg-surface px-4 py-4">
        <div className="flex items-start gap-3">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-accent/10 text-lg font-bold text-accent">{initials(c.patientName)}</span>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-extrabold leading-tight tracking-tight text-ink">{c.patientName}</h1>
            <div className="mt-0.5 truncate text-sm text-muted">
              {c.caseTypeRaw ?? c.caseTypes.map((t) => cfg.optionLabel('case_type', t)).join(', ')}
              {c.receivedThrough ? ` · ${cfg.optionLabel('received_through', c.receivedThrough)}` : ''}
            </div>
            {due && (
              <span className={`mt-2 inline-block rounded-md border px-2 py-0.5 text-[11px] font-bold uppercase ${due.tone === 'bad' ? 'border-status-urgent-fg/40 bg-status-urgent-bg text-status-urgent-fg' : due.tone === 'warn' ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-line bg-surface-2 text-muted'}`}>{due.text}</span>
            )}
          </div>
          <span className="shrink-0 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-sm font-semibold text-accent">
            <span className="mr-1 inline-block h-2 w-2 rounded-full bg-accent align-middle" />{stage?.name ?? c.stageKey}
          </span>
        </div>
        <div className="mt-3 flex items-center gap-2 text-sm">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-surface-2 text-[11px] font-bold text-muted">DR</span>
          <span className="min-w-0 flex-1 truncate text-muted">Assigned doctor: <span className="font-medium text-ink">{c.doctorName ?? c.assignedToName ?? 'Unassigned'}</span></span>
          {c.iprStatus !== 'not_required' && (
            <span className="shrink-0 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-800">● IPR: {WF_REQ_LABELS[c.iprStatus]}</span>
          )}
        </div>
      </div>

      {/* Production pipeline stepper */}
      <div className="border-b border-line bg-surface px-4 py-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold text-accent">● Production pipeline</span>
          {stepIdx >= 0 && <span className="rounded-full bg-accent/10 px-2.5 py-0.5 text-xs font-semibold text-accent">Step {stepIdx + 1} of {path.length}</span>}
        </div>
        <ol className="no-scrollbar flex items-center gap-1 overflow-x-auto whitespace-nowrap">
          {path.map((st, i) => {
            const done = i < stepIdx || (visited.has(st.key) && i !== stepIdx);
            const cur = i === stepIdx;
            // Passing through the approval stage is not the same as being approved. A tick here
            // read as "the doctor approved" when nobody had; the step now shows its real state.
            const unapproved = st.key === R.doctorApproval && done && !cur && !approvalGranted(c);
            return (
              <li key={st.key} className="flex items-center">
                <button
                  onClick={() => canMove && !cur && act('move', { toStage: st.key })}
                  className={`tap rounded-full border px-3 py-1.5 text-sm font-medium ${cur ? 'border-accent bg-accent text-accent-fg' : unapproved ? 'border-status-warn-fg/40 bg-status-warn-bg text-status-warn-fg' : done ? 'border-accent/40 bg-accent/10 text-accent' : 'border-line text-muted'}`}
                >
                  {unapproved ? '⏳ ' : done && !cur ? '✓ ' : ''}{st.name}{unapproved ? ' (pending)' : ''}
                </button>
                {i < path.length - 1 && <span className="mx-1 h-px w-3 bg-line" />}
              </li>
            );
          })}
        </ol>
        {approvalHoldsPrinting(c, visited, path, stepIdx) && (
          <div className="mt-2 rounded-lg border border-status-warn-fg/30 bg-status-warn-bg px-3 py-2 text-xs leading-relaxed text-status-warn-fg">
            <b>Waiting on approval.</b> The doctor has not approved the setup yet, so this case cannot enter Printing.
            Ask the doctor to approve it in the portal, or record a production approval if the lab is proceeding on its own authority.
            {can('workflow:approve') && (
              <button type="button" onClick={() => act('approve')} className="ml-2 font-bold underline">Record approval</button>
            )}
          </div>
        )}
      </div>

      {/* Action strip */}
      <div className="no-scrollbar flex items-center gap-2 overflow-x-auto whitespace-nowrap border-b border-line bg-bg px-4 py-3">
        {canMove && !stage?.isTerminal && c.stageKey !== 'on_hold' && (
          <button onClick={() => act('move')} className="tap shrink-0 rounded-full bg-accent px-4 py-2.5 text-sm font-bold text-accent-fg">→ Move stage</button>
        )}
        {canMove && c.stageKey === 'on_hold' && <button onClick={() => act('resume')} className="tap shrink-0 rounded-full bg-accent px-4 py-2.5 text-sm font-bold text-accent-fg">Resume</button>}
        {canMove && !stage?.isTerminal && c.stageKey !== 'on_hold' && <Chip onClick={() => act('hold')}>Hold</Chip>}
        {can('workflow:update') && <Chip onClick={() => act('assign')}>Assign</Chip>}
        {can('workflow:update') && <Chip onClick={() => act('note')}>Add note</Chip>}
        {can('workflow:update') && <Chip onClick={() => act('upload')}>Upload</Chip>}
        {can('workflow:approve') && !stage?.isTerminal && <Chip onClick={() => act('approve')}>Approval</Chip>}
        {can('workflow:production') && !stage?.isTerminal && <Chip onClick={() => act('production')}>Production</Chip>}
        {can('workflow:dispatch') && (c.stageKey === R.packaging || c.stageKey === R.dispatch) && <Chip onClick={() => act('dispatch')}>Dispatch</Chip>}
        {/* Last on the strip, and the only one in danger colours - a destructive action should take
            a deliberate scroll to reach, not sit under a thumb resting on the screen. */}
        {can('workflow:cancel') && !c.deletedAt && (
          <button
            onClick={onDelete}
            className="tap shrink-0 rounded-full bg-status-urgent-bg px-4 py-2.5 text-sm font-semibold text-status-urgent-fg"
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

function Chip({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return <button onClick={onClick} className="tap shrink-0 rounded-full border border-line bg-surface px-4 py-2.5 text-sm font-medium text-ink">{children}</button>;
}

function MobileOverview({ ws, onEdit }: { ws: WfCaseWorkspace; onEdit?: () => void }) {
  const cfg = useWfConfig();
  const pricing = useAlignerPricing();
  const c = ws.case;
  const recent = [...ws.events].sort((a, b) => Date.parse(b.at) - Date.parse(a.at)).slice(0, 5);
  return (
    <div className="space-y-4 lg:hidden">
      {PRODUCTION_COUNT_STAGES.includes(c.stageKey) && (
        <MCard icon="🏭" title="Aligners in production" action={<ProformaPrintButton caseId={c.id} label="Proforma" />}>
          <ProductionCounters c={c} compact />
        </MCard>
      )}
      <MCard icon="👤" title="Patient & clinic details" action={onEdit ? <button onClick={onEdit} className="tap text-sm font-semibold text-accent">Edit</button> : null}>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          <KV k="Patient" v={c.patientName} strong />
          <KV k="Patient ID" v={c.patientCode} />
          <KV k="Contact / phone" v={c.phone} />
          <KV k="WhatsApp" v={c.whatsapp} />
          <KV k="Email" v={c.email} />
          <KV k="Group ID" v={c.groupId} mono />
        </div>
        <div className="my-3 border-t border-line" />
        <KV k="Clinic" v={c.clinicName ? `🏥 ${c.clinicName}${c.branch ? ` · ${c.branch}` : ''}` : null} strong />
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
          <KV k="Received via" v={c.receivedThroughRaw ?? cfg.optionLabel('received_through', c.receivedThrough)} />
          <KV k="Case type" v={c.caseTypeRaw ?? c.caseTypes.map((t) => cfg.optionLabel('case_type', t)).join(', ')} />
          <KV k="Created at" v={fmtDT(c.createdAt)} />
          <KV k="Grade / punching" v={[c.grade, c.punching].filter(Boolean).join(' / ') || null} />
          {typeof c.trays === 'number' && c.trays > 0 && <KV k="Aligners" v={`${alignerSummary(c)}${c.currentTray ? ` · on tray ${c.currentTray}` : ''}`} />}
          {typeof c.trays === 'number' && c.trays > 0 && <KV k="Clinic price" v={ws.invoice ? `PKR ${(ws.invoice.totalPaisa / 100).toLocaleString()} (${ws.invoice.invoiceNumber})` : `PKR ${alignerQuote(c.trays, pricing, alignerDiscountOf(c)).netRupees.toLocaleString()}${alignerDiscountOf(c) ? ' after discount' : ''}`} />}
        </div>
        <div className="mt-4 rounded-2xl bg-navy p-4 text-white">
          <MobilePatientAccess caseId={c.id} />
        </div>
      </MCard>

      <MCard icon="📄" title="Notes & clinical instructions">
        <div className="space-y-3">
          <KV k="Case notes" v={c.notes} italicEmpty />
          <KV k="Production instructions" v={c.productionInstructions} italicEmpty />
          <KV k="Internal instructions" v={c.internalInstructions} italicEmpty />
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">IPR & etching details</div>
            {c.iprNotes || c.etchingNotes ? (
              <div className="mt-1.5 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
                ● {[c.iprNotes && `IPR: ${c.iprNotes}`, c.etchingNotes && `Etching: ${c.etchingNotes}`].filter(Boolean).join(' · ')}
              </div>
            ) : (
              <div className="mt-1 text-sm italic text-muted">None provided</div>
            )}
          </div>
        </div>
      </MCard>

      <MCard icon="🕓" title="Stage history" action={<span className="text-xs text-muted">Real-time log</span>}>
        <ul className="space-y-3">
          {recent.map((e) => (
            <li key={e.id} className="flex gap-3">
              <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-accent" />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-semibold text-ink">{e.eventType === 'stage_change' ? `${cfg.stageName(e.toStage)}` : e.comment ?? e.eventType}</span>
                  <span className="shrink-0 text-xs text-muted">{ageLabel(e.at)}</span>
                </div>
                <div className="text-xs text-muted">Updated by {e.actorName ?? 'system'}</div>
              </div>
            </li>
          ))}
          {!recent.length && <li className="text-sm italic text-muted">No activity yet</li>}
        </ul>
      </MCard>
    </div>
  );
}

function MCard({ icon, title, action, children }: { icon: string; title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-line bg-surface p-4 shadow-level-1">
      <div className="mb-3 flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-accent/10 text-sm text-accent">{icon}</span>
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        <span className="ml-auto">{action}</span>
      </div>
      {children}
    </section>
  );
}

function KV({ k, v, mono, strong, italicEmpty }: { k: string; v: React.ReactNode; mono?: boolean; strong?: boolean; italicEmpty?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">{k}</div>
      <div className={`mt-0.5 break-words text-sm ${v ? (strong ? 'font-semibold text-ink' : 'text-ink') : italicEmpty ? 'italic text-muted' : 'text-muted'} ${mono ? 'font-mono' : ''}`}>{v ?? (italicEmpty ? 'None provided' : '-')}</div>
    </div>
  );
}

function MobilePatientAccess({ caseId }: { caseId: string }) {
  // Reuse the full card's logic but present it in the dark "app access" style.
  return (
    <div className="[&_.rounded-lg]:!border-0 [&_.rounded-lg]:!bg-transparent [&_.rounded-lg]:!p-0 [&_.rounded-lg]:!shadow-none [&_.text-muted]:!text-white/70 [&_.text-ink]:!text-white [&_button.bg-primary]:!bg-accent [&_button.bg-primary]:!text-accent-fg [&_button.bg-primary]:!w-full [&_button.bg-primary]:!py-3 [&_button.bg-primary]:!rounded-xl [&_button.border-line]:!border-white/30 [&_button.border-line]:!text-white">
      <PatientAccessCard caseId={caseId} />
    </div>
  );
}
